import { filter } from '@atlaskit/adf-utils/traverse';

import { isMigratedMacroImplEnabled } from '@confluence/macro-utils/entry-points/evaluateMacroEnablement.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import { blogPostsPreloadHandler } from './handlers/blogPostsPreloadHandler.ts';
import { contentByLabelPreloadHandler } from './handlers/contentByLabelPreloadHandler.ts';
import { contributorsPreloadHandler } from './handlers/contributorsPreloadHandler.ts';
import { pageIncludePreloadHandler } from './handlers/pageIncludePreloadHandler.ts';
import { recentlyUpdatedPreloadHandler } from './handlers/recentlyUpdatedPreloadHandler.ts';
import { pagetreePreloadHandler } from './handlers/pagetreePreloadHandler.ts';
import { pagetreeSearchPreloadHandler } from './handlers/pagetreeSearchPreloadHandler.ts';
import { popularLabelsPreloadHandler } from './handlers/popularLabelsPreloadHandler.ts';

/**
 * An ADF extension node representing a macro instance in the document.
 */
export interface MacroContentNode {
	type: string;
	attrs?: {
		extensionKey?: string;
		parameters?: {
			macroParams?: Record<string, { value: string }>;
		};
	};
}

/**
 * Context passed to each macro preload handler, containing information
 * about the content being preloaded.
 */
export interface MacroPreloadContext {
	contentId: string;
	spaceKey: string;
}

/**
 * A handler that knows how to preload data for a specific macro type.
 *
 * Each macro that supports preloading should implement this interface and
 * register its handler with `preloadMacroData`.
 */
export interface MacroPreloadHandler {
	/**
	 * The ADF extension key that identifies this macro type
	 * (e.g. 'contributors', 'detailssummary').
	 */
	extensionKey: string;

	/**
	 * Preloads data for a single instance of this macro found in the content.
	 *
	 * @param node - The ADF extension node matching this handler's extensionKey
	 * @param context - Content context (contentId, spaceKey, etc.)
	 * @returns A promise that resolves when preloading is complete
	 */
	preload: (node: MacroContentNode, context: MacroPreloadContext) => Promise<void>;
}

const EXTENSION_TYPES = new Set(['extension', 'bodiedExtension', 'inlineExtension']);

/**
 * Returns a map of extensionKey → handler for all active macro preload
 * handlers based on current experiment gates.
 */
const getActiveHandlers = (): Map<string, MacroPreloadHandler> => {
	const handlers = new Map<string, MacroPreloadHandler>();

	if (isMigratedMacroImplEnabled(blogPostsPreloadHandler.extensionKey)) {
		handlers.set(blogPostsPreloadHandler.extensionKey, blogPostsPreloadHandler);
	}

	if (isMigratedMacroImplEnabled(contentByLabelPreloadHandler.extensionKey)) {
		handlers.set(contentByLabelPreloadHandler.extensionKey, contentByLabelPreloadHandler);
	}

	if (isMigratedMacroImplEnabled(contributorsPreloadHandler.extensionKey)) {
		handlers.set(contributorsPreloadHandler.extensionKey, contributorsPreloadHandler);
	}

	if (isMigratedMacroImplEnabled(pageIncludePreloadHandler.extensionKey)) {
		handlers.set(pageIncludePreloadHandler.extensionKey, pageIncludePreloadHandler);
	}

	if (isMigratedMacroImplEnabled(recentlyUpdatedPreloadHandler.extensionKey)) {
		handlers.set(recentlyUpdatedPreloadHandler.extensionKey, recentlyUpdatedPreloadHandler);
	}

	if (isMigratedMacroImplEnabled(popularLabelsPreloadHandler.extensionKey)) {
		handlers.set(popularLabelsPreloadHandler.extensionKey, popularLabelsPreloadHandler);
	}

	if (isMigratedMacroImplEnabled(pagetreePreloadHandler.extensionKey)) {
		handlers.set(pagetreePreloadHandler.extensionKey, pagetreePreloadHandler);
	}

	if (isMigratedMacroImplEnabled(pagetreeSearchPreloadHandler.extensionKey)) {
		handlers.set(pagetreeSearchPreloadHandler.extensionKey, pagetreeSearchPreloadHandler);
	}

	return handlers;
};

/**
 * Resolves content nodes (which may be a promise), validates the body structure,
 * and parses the ADF JSON into a document object.
 *
 * Returns null if the content nodes are invalid, the representation is not
 * 'atlas_doc_format', or the ADF JSON cannot be parsed.
 */
const resolveAndParseADF = async (contentNodes: any): Promise<any | null> => {
	const resolvedNodes =
		contentNodes && typeof contentNodes.then === 'function' ? await contentNodes : contentNodes;

	if (!resolvedNodes?.[0]?.body?.dynamic?.value) {
		return null;
	}

	if (resolvedNodes[0].body.dynamic.representation !== 'atlas_doc_format') {
		return null;
	}

	try {
		return JSON.parse(resolvedNodes[0].body.dynamic.value);
	} catch {
		return null;
	}
};

/**
 * Parses the comma-separated blocklist string from Statsig into a list of
 * trimmed, non-empty regex pattern strings.
 */
const getBlocklist = (): string[] => {
	const raw = StatsigConfigurations.getString('confluence-extensions-server-render-blocklist', '');
	const entries = raw.split(',');
	const result: string[] = [];
	for (let i = 0; i < entries.length; i++) {
		const trimmed = entries[i].trim();
		if (trimmed) {
			result.push(trimmed);
		}
	}
	return result;
};

/**
 * Returns true when `extensionKey` is present in the blocklist.
 *
 * The blocklist is sourced from a single Statsig dynamic config
 * (`confluence-extensions-server-render-blocklist`) so it controls both SSR
 * macro query preloading and supplementary macro data preloading.
 */
const isBlocklisted = (blocklist: string[], extensionKey: string): boolean => {
	if (blocklist.length === 0) {
		return false;
	}

	return blocklist.includes(extensionKey);
};

/**
 * Default cap on the number of extensions preloaded per content node when
 * the `cc_ssr_max_extensions_to_preload` Statsig dynamic config is not set.
 */
const DEFAULT_MAX_EXTENSIONS_TO_PRELOAD = 8;

/**
 * Default cap on the number of ADF nodes traversed when scanning the
 * document for preloadable extensions, used when the
 * `cc_ssr_macros_max_nodes_to_iterative_over` Statsig dynamic config is
 * not set.
 */
const DEFAULT_MAX_NODES_TO_ITERATE_OVER = -1;

/**
 * Returns the maximum number of extensions to preload, sourced from the
 * `cc_ssr_max_extensions_to_preload` Statsig dynamic config.
 *
 * A non-positive or non-finite value is treated as "no limit" and is
 * returned as the sentinel `-1` so callers can opt out by configuring `0`
 * (or any negative value).
 */
const getMaxExtensionsToPreload = (): number => {
	const value = StatsigConfigurations.getNumber(
		'cc_ssr_max_extensions_to_preload',
		DEFAULT_MAX_EXTENSIONS_TO_PRELOAD,
	);

	if (!Number.isFinite(value) || value < 0) {
		return -1;
	}
	return Math.floor(value);
};

/**
 * Returns the maximum number of ADF nodes to iterate over when searching
 * for preloadable extension nodes, sourced from the
 * `cc_ssr_macros_max_nodes_to_iterative_over` Statsig dynamic config.
 *
 * A non-positive or non-finite value is treated as "no limit" and is
 * returned as the sentinel `-1` so callers can opt out by configuring `0`
 * (or any negative value).
 */
const getMaxNodesToIterateOver = (): number => {
	const value = StatsigConfigurations.getNumber(
		'cc_ssr_macros_max_nodes_to_iterative_over',
		DEFAULT_MAX_NODES_TO_ITERATE_OVER,
	);

	if (!Number.isFinite(value) || value < 0) {
		return -1;
	}
	return Math.floor(value);
};

/**
 * Filters extension nodes from the parsed ADF document that have a
 * registered preload handler and are not excluded by the SSR extension
 * blocklist.
 *
 * Two Statsig dynamic configs control the work done here:
 * - `cc_ssr_macros_max_nodes_to_iterative_over` caps the number of ADF
 *   nodes visited during traversal (default -1). A value of `-1` means
 *   no limit.
 * - `cc_ssr_max_extensions_to_preload` caps the number of matched
 *   extensions returned (default 8). A value of `-1` means no limit.
 */
const getPreloadableMacroNodes = (
	documentADF: any,
	handlersByKey: Map<string, MacroPreloadHandler>,
): MacroContentNode[] => {
	const blocklist = getBlocklist();
	const maxNodesToIterateOver = getMaxNodesToIterateOver();

	// Track how many ADF nodes the traversal has visited so we can short-
	// circuit once `cc_ssr_macros_max_nodes_to_iterative_over` is reached.
	// `filter` from @atlaskit/adf-utils/traverse walks the full document tree
	// (the document object itself is not an array), unlike `Array#filter`.
	let visited = 0;
	const matched = filter(documentADF, (node: any) => {
		if (maxNodesToIterateOver !== -1 && visited >= maxNodesToIterateOver) {
			return false;
		}
		visited++;

		const extensionKey = node.attrs?.extensionKey;
		return (
			EXTENSION_TYPES.has(node.type) &&
			typeof extensionKey === 'string' &&
			handlersByKey.has(extensionKey) &&
			!isBlocklisted(blocklist, extensionKey)
		);
	}) as MacroContentNode[];

	const maxExtensionsToPreload = getMaxExtensionsToPreload();
	if (maxExtensionsToPreload === -1 || matched.length <= maxExtensionsToPreload) {
		return matched;
	}
	return matched.slice(0, maxExtensionsToPreload);
};

/**
 * Preloads data for all registered macro types found in the content.
 *
 * This function centralizes the macro preloading pattern by:
 * 1. Determining which macro handlers are active (based on experiment gates)
 * 2. Resolving content nodes (which may be a raw value or a Promise)
 * 3. Parsing the ADF document once
 * 4. Filtering for extension nodes that have a registered handler
 * 5. Calling the corresponding handler for each matched node in parallel
 *
 * @param params.contentNodes - The raw content nodes (or a Promise resolving to them)
 * @param params.context - Content context passed through to each handler
 */
export const preloadMacroData = async ({
	contentNodes,
	context,
}: {
	contentNodes: any;
	context: MacroPreloadContext;
}): Promise<void> => {
	const handlersByKey = getActiveHandlers();
	if (handlersByKey.size === 0) {
		return;
	}

	const documentADF = await resolveAndParseADF(contentNodes);
	if (!documentADF) {
		return;
	}

	const matchedNodes = getPreloadableMacroNodes(documentADF, handlersByKey);
	if (matchedNodes.length === 0) {
		return;
	}

	// eslint-disable-next-line check-preloaders/no-promise-all
	await Promise.all(
		matchedNodes.map((node) => {
			const handler = handlersByKey.get(node.attrs!.extensionKey!)!;
			return handler.preload(node, context);
		}),
	);
};
